/**
 * Cookie-based authentication utility for cross-portal authentication
 *
 * This utility helps share authentication tokens between the main portal
 * and supply portal using cookies. Cookies are scoped by environment to
 * prevent conflicts when users log in to different environments (prod, prep, demo)
 * in the same browser.
 */

/**
 * Detect the current environment from the hostname
 * Returns: 'prod', 'prep', 'demo', 'test', 'staging', 'dev', or 'localhost'
 */
const getEnvironment = () => {
  const hostname = window.location.hostname.toLowerCase();

  // For localhost, return 'localhost'
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'localhost';
  }

  // Check for production (my.biofuelcircle.com or supply.biofuelcircle.com)
  // These are the main production domains
  if (hostname === 'my.biofuelcircle.com' || hostname === 'supply.biofuelcircle.com') {
    return 'prod';
  }

  // Check for other environments by hostname
  // This is more reliable than checking href
  if (hostname.includes('prep')) {
    return 'prep';
  }
  if (hostname.includes('demo')) {
    return 'demo';
  }
  if (hostname.includes('test')) {
    return 'test';
  }
  if (hostname.includes('staging')) {
    return 'staging';
  }
  if (hostname.includes('dev')) {
    return 'dev';
  }

  // Default to prod if no match (fallback for production-like domains)
  return 'prod';
};

/**
 * Get environment-scoped cookie name
 * Example: 'bfc_accessToken' + '_prod' = 'bfc_accessToken_prod'
 */
const getScopedCookieName = (baseName) => {
  const env = getEnvironment();
  // For localhost, don't add suffix to maintain backward compatibility during development
  if (env === 'localhost') {
    return baseName;
  }
  return `${baseName}_${env}`;
};

/**
 * Get the base domain from a URL
 * Example: supply.biofuelcircle.com -> .biofuelcircle.com
 */
const getBaseDomain = () => {
  const hostname = window.location.hostname.toLowerCase();

  // For localhost, return empty string (cookies work on same origin)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '';
  }

  if (hostname.endsWith('.biofuelcircle.com')) {
    return '.biofuelcircle.com';
  }

  // Fallback: split by dots and get the last two parts for generic domains
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    // Return domain like .example.com
    return '.' + parts.slice(-2).join('.');
  }

  return '';
};

/**
 * Set a cookie with authentication data
 * Cookies are automatically scoped by environment to prevent cross-environment conflicts
 */
export const setAuthCookie = (name, value, days = 1) => {
  try {
    const scopedName = getScopedCookieName(name);
    const domain = getBaseDomain();
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

    let cookieString = `${scopedName}=${encodeURIComponent(
      value,
    )}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    // Only set domain if we have a valid domain (not localhost)
    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    document.cookie = cookieString;
    return true;
  } catch (error) {
    console.error('Error setting auth cookie:', error);
    return false;
  }
};

/**
 * Get a cookie value by name
 * Automatically uses environment-scoped cookie name
 */
export const getAuthCookie = (name) => {
  try {
    const scopedName = getScopedCookieName(name);
    const nameEQ = scopedName + '=';
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting auth cookie:', error);
    return null;
  }
};

/**
 * Delete a cookie
 * Automatically uses environment-scoped cookie name
 */
export const deleteAuthCookie = (name) => {
  try {
    const scopedName = getScopedCookieName(name);
    const domain = getBaseDomain();
    let cookieString = `${scopedName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    document.cookie = cookieString;
    return true;
  } catch (error) {
    console.error('Error deleting auth cookie:', error);
    return false;
  }
};

/**
 * Set all authentication tokens in cookies
 */
export const setAuthCookies = async (authData) => {
  const { token, refreshToken, tokenExpiry, pid, sid, loginStatusId } = authData;

  if (token) {
    setAuthCookie('bfc_accessToken', token, 1);
  }
  if (refreshToken) {
    setAuthCookie('bfc_refreshToken', refreshToken, 7); // Refresh token lasts longer
  }
  if (tokenExpiry) {
    setAuthCookie('bfc_tokenExpiry', tokenExpiry, 1);
  }
  if (pid) {
    setAuthCookie('bfc_pid', pid, 7);
  }
  if (sid) {
    setAuthCookie('bfc_sid', sid, 7);
  }
  if (loginStatusId) {
    setAuthCookie('bfc_loginStatusId', loginStatusId, 1);
  }
};

/**
 * Get all authentication tokens from cookies
 */
export const getAuthCookies = () => {
  return {
    token: getAuthCookie('bfc_accessToken'),
    refreshToken: getAuthCookie('bfc_refreshToken'),
    tokenExpiry: getAuthCookie('bfc_tokenExpiry'),
    pid: getAuthCookie('bfc_pid'),
    sid: getAuthCookie('bfc_sid'),
    loginStatusId: getAuthCookie('bfc_loginStatusId'),
  };
};

/**
 * Clear all authentication cookies for the current environment
 * This function clears environment-scoped cookies and also attempts to clear
 * any legacy cookies (without environment suffix) for backward compatibility
 */
export const clearAuthCookies = () => {
  const baseCookieNames = [
    'bfc_accessToken',
    'bfc_refreshToken',
    'bfc_tokenExpiry',
    'bfc_pid',
    'bfc_sid',
    'bfc_loginStatusId',
  ];

  // Clear environment-scoped cookies (current approach)
  baseCookieNames.forEach((name) => {
    deleteAuthCookie(name);
  });

  // Also clear legacy cookies (without environment suffix) for backward compatibility
  // This helps clean up any old cookies that might exist
  try {
    const domain = getBaseDomain();
    const expires = 'Thu, 01 Jan 1970 00:00:00 UTC';

    baseCookieNames.forEach((name) => {
      // Try clearing without domain
      document.cookie = `${name}=; expires=${expires}; path=/`;

      // Try clearing with base domain
      if (domain) {
        document.cookie = `${name}=; expires=${expires}; path=/; domain=${domain}`;
      }

      // Try clearing with empty domain
      document.cookie = `${name}=; expires=${expires}; path=/; domain=`;
    });
  } catch (error) {
    console.error('Error clearing legacy cookies:', error);
  }
};
